import { motion, useInView } from "framer-motion";
import { useRef } from "react";

const intro = [
  "SconeCORE Enterprises is a leading",
  "manufacturer and supplier of premium",
  "plastic processing products — serving",
  "diverse industries across India.",
];

const chapters = [
  {
    num: "01",
    tag: "The Portfolio",
    lines: [
      "Quick lime. Anti-moisture powder.",
      "Desiccant masterbatch. Calcium filler.",
      "Colour masterbatch. Each engineered",
      "for superior performance and reliability.",
    ],
  },
  {
    num: "02",
    tag: "Our Legacy",
    lines: [
      "One Microns — our established",
      "manufacturing division — has served",
      "the industry since 2011 with excellence",
      "and innovation. SconeCORE Enterprises",
      "expands that foundation to serve our",
      "growing customer base across regions.",
    ],
  },
  {
    num: "03",
    tag: "Precision",
    lines: [
      "From >93% CaO quick lime to",
      "high-thermal colour masterbatches,",
      "our additives are engineered for the",
      "extruder, the mould, and the shelf.",
    ],
  },
  {
    num: "04",
    tag: "Partnership",
    lines: [
      "We supply factories, not orders.",
      "Volume discounts, tailored dosages,",
      "delivery — a proprietor who picks",
      "up the phone. Arnav Malkani.",
    ],
  },
];

const commitments = [
  "Products that meet international quality standards.",
  "Reliable supply chains and consistent delivery.",
  "Customers supported with deep technical expertise.",
  "Continuous innovation and operational excellence.",
  "Sustainable and responsible manufacturing practices.",
];

const Line = ({ children, delay = 0 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.01, margin: "0px 0px -5% 0px" });
  return (
    <span ref={ref} className="reveal-mask">
      <motion.span
        initial={{ y: "110%" }}
        animate={inView ? { y: "0%" } : { y: "110%" }}
        transition={{ duration: 0.9, delay, ease: [0.76, 0, 0.24, 1] }}
        className="block"
      >
        {children}
      </motion.span>
    </span>
  );
};

const Manifesto = () => {
  return (
    <section id="manifesto" className="relative bg-[#faf9f6] py-24 lg:py-40 px-5 lg:px-10">
      <div className="max-w-[1400px] mx-auto">
        <div className="section-rule overline text-[#0A2A1E] mb-16 lg:mb-24">
          Ethos — Five Chapters
        </div>

        {/* Intro chapter — 00 */}
        <div className="grid grid-cols-12 gap-6 lg:gap-12 items-start mb-24 lg:mb-32">
          <div className="col-span-12 md:col-span-4 lg:col-span-3">
            <div className="font-display text-[#0A2A1E] text-7xl lg:text-8xl leading-none">
              00
            </div>
            <div className="overline text-[#1E3B30] mt-3">— The Preface</div>
          </div>
          <div className="col-span-12 md:col-span-8 lg:col-span-9">
            <div className="font-display text-[#0A2A1E] text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-[-0.02em]">
              {intro.map((ln, i) => (
                <Line key={i} delay={i * 0.08}>
                  {ln}
                </Line>
              ))}
            </div>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, delay: 0.4 }}
              className="mt-8 text-base md:text-lg text-[#1E3B30] leading-relaxed max-w-2xl"
            >
              We pride ourselves on delivering high-quality materials that meet
              the exacting standards of modern manufacturing.
            </motion.p>
          </div>
        </div>

        <div className="space-y-24 lg:space-y-32">
          {chapters.map((c) => (
            <div key={c.num} className="grid grid-cols-12 gap-6 lg:gap-12 items-start">
              <div className="col-span-12 md:col-span-4 lg:col-span-3">
                <div className="font-display text-[#0A2A1E] text-7xl lg:text-8xl leading-none">
                  {c.num}
                </div>
                <div className="overline text-[#1E3B30] mt-3">— {c.tag}</div>
              </div>
              <div className="col-span-12 md:col-span-8 lg:col-span-9">
                <div className="font-display text-[#0A2A1E] text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-[-0.02em]">
                  {c.lines.map((ln, i) => (
                    <Line key={i} delay={i * 0.08}>
                      {ln}
                    </Line>
                  ))}
                </div>

                {/* Chapter 02 gets the "since 2011" badge */}
                {c.num === "02" && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: 0.4 }}
                    className="mt-8 inline-flex items-center gap-3 border border-[#0A2A1E]/40 px-4 py-2"
                  >
                    <span className="w-2 h-2 bg-[#C6F035]" />
                    <span className="overline text-[#0A2A1E]">
                      One Microns · Since 2011
                    </span>
                  </motion.div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Commitment list — the manifesto climax */}
        <div className="mt-32 lg:mt-40 grid grid-cols-12 gap-6 lg:gap-12 items-start">
          <div className="col-span-12 md:col-span-4 lg:col-span-3">
            <div className="font-display text-[#0A2A1E] text-7xl lg:text-8xl leading-none">
              05
            </div>
            <div className="overline text-[#1E3B30] mt-3">
              — The Commitment
            </div>
          </div>
          <div className="col-span-12 md:col-span-8 lg:col-span-9">
            <div className="font-display text-[#0A2A1E] text-3xl md:text-4xl lg:text-5xl leading-[1.1] tracking-[-0.02em] mb-10 max-w-3xl">
              <Line>We are committed to five things,</Line>
              <Line delay={0.08}>
                <span className="font-display-italic">quietly</span> and
                without exception.
              </Line>
            </div>

            <ul className="border-t border-[#0A2A1E]/25">
              {commitments.map((c, i) => (
                <motion.li
                  key={c}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.7, delay: i * 0.08 }}
                  className="grid grid-cols-12 gap-4 py-6 border-b border-[#0A2A1E]/25 items-center group"
                >
                  <div className="col-span-2 md:col-span-1 overline text-[#0A2A1E]/60">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <div className="col-span-10 md:col-span-10 font-display text-2xl md:text-3xl lg:text-4xl text-[#0A2A1E] leading-tight">
                    {c}
                  </div>
                  <div className="hidden md:flex col-span-1 justify-end">
                    <span className="w-2 h-2 bg-[#C6F035] rotate-45 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  </div>
                </motion.li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Manifesto;
